import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faDog } from '@fortawesome/pro-solid-svg-icons/faDog';
import { faCat } from '@fortawesome/pro-solid-svg-icons/faCat';
import { faPaw } from '@fortawesome/pro-solid-svg-icons/faPaw';
import { faThumbsUp } from '@fortawesome/pro-solid-svg-icons/faThumbsUp';
import { faFlag } from '@fortawesome/pro-solid-svg-icons/faFlag';

import {
  Parent as BaseParent,
  ParentService,
  JobService,
  SitterService,
  Sitter,
  LanguageService,
  Language,
  AffiliationService,
  LocalGroupService,
  SchoolService,
  ParentAffiliationService,
  ParentLanguagesService,
  ParentLocalGroupsService,
  ParentSchoolsService,
  SpecialExperience,
  SpecialExperiencesService,
  AxiosError,
  ContentFlagService,
  PROFILE_FLAG_REASONS,
} from 'us-web-services';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import {
  formatDateRange,
  getMonthDayString,
} from 'us-react-components/dist/utils/date';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

interface ParentBadge {
  type?: string;
  label?: string;
  description?: string;
  active?: boolean;
  url?: string;
}

interface ParentExtras extends Omit<BaseParent, 'relationship'> {
  badges?: ParentBadge[];
  languages?: Array<{ id?: number; languageId?: number; name?: string }>;
  emergencyName?: string;
  emergencyPhone?: string;
  pediatricianName?: string;
  pediatricianPhone?: string;
  covidBadgeDisplay?: boolean;
  userStats?: { lastAccessLabel?: string };
  relationship?: {
    blocked?: boolean;
    bookingCount?: number;
    blockedId?: number | null;
  };
}

interface JobBookingShape {
  start?: string;
  end?: string;
  rate?: number;
  paymentType?: string;
  numberOfChildren?: number;
  kidsAges?: string;
  description?: string;
  typeId?: number;
  address?: { city?: string; state?: string };
}

interface Job {
  id?: number;
  recurring?: boolean;
  availability?: string;
  booking?: JobBookingShape;
}

const BOOKING_TYPE_CODES: Record<number, string> = {
  1: 'childCare',
  2: 'petCare',
  3: 'seniorCare',
  4: 'householdServices',
};

const getJobTitle = (job: Job): string => {
  if (job.recurring) {
    const start = getMonthDayString(job.booking?.start);
    return `${job.availability ?? ''}${start ? `, Starting ${start}` : ''}`.trim();
  }
  return formatDateRange(job.booking?.start, job.booking?.end);
};

const getChildrenLine = (booking?: JobBookingShape): string => {
  if (!booking) return '';
  const n = booking.numberOfChildren ?? 0;
  if (!n) return '';
  const code = BOOKING_TYPE_CODES[booking.typeId ?? 0];
  if (code === 'petCare') return `${n} ${n === 1 ? 'pet' : 'pets'}`;
  if (code === 'seniorCare') return `${n} ${n === 1 ? 'senior' : 'seniors'}`;
  if (code === 'householdServices') return '';
  const ages = booking.kidsAges ? ` (${booking.kidsAges})` : '';
  return `${n} child${n === 1 ? '' : 'ren'}${ages}`;
};

const BADGE_ICON: Record<string, string> = {
  facebook: 'fa-brands fa-facebook',
  sitter: 'fa-solid fa-user',
  sitterMember: 'fa-solid fa-id-card',
  'member-since': 'fa-solid fa-id-card',
  member: 'fa-solid fa-id-card',
  'review-received': 'fa-solid fa-comments',
  'review-given': 'fa-solid fa-pen-to-square',
  review: 'fa-solid fa-pen-to-square',
  reviewCount: 'fa-solid fa-comments',
  vaccine: 'fa-solid fa-syringe',
  covidBadge: 'fa-solid fa-syringe',
  referrals: 'fa-solid fa-share-nodes',
  acceptsCreditCards: 'fa-solid fa-credit-card',
  cc: 'fa-solid fa-credit-card',
  backgroundCheck: 'fa-solid fa-shield-check',
  corporate: 'fa-solid fa-building',
  cpr: 'fa-solid fa-heart-pulse',
  family: 'fa-solid fa-house-user',
  repeatFamilies: 'fa-solid fa-rotate',
  repeated_sitter: 'fa-solid fa-rotate',
  friend: 'fa-solid fa-user-group',
  messageResponse: 'fa-solid fa-message',
  response: 'fa-solid fa-clock',
  responseTime: 'fa-solid fa-clock',
  reliable: 'fa-solid fa-circle-check',
  trustedBy: 'fa-solid fa-handshake',
  event: 'fa-solid fa-award',
};

// -- ParentSnippet ---------------------------------------------------------
const ParentSnippet = ({
  parent,
  canEdit,
  onFlag,
  onShare,
  onBlockToggle,
  viewerIsSitter,
}: {
  parent: ParentExtras;
  canEdit: boolean;
  onFlag: () => void;
  onShare: () => void;
  onBlockToggle: (blocked: boolean) => void;
  viewerIsSitter: boolean;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const kidsCount = (parent.kids ?? []).length;
  const blocked = !!parent.relationship?.blocked;
  return (
    <div className='grid grid-cols-1 md:grid-cols-12 mb-4 gap-3'>
      <h1 className='md:col-span-3 hidden md:block text-2xl'>{parent.firstName}&apos;s Profile</h1>
      <div className='md:col-span-6 text-center'>
        {parent.pictureUrl && (
          <img
            src={parent.pictureUrl}
            alt={parent.name ?? parent.firstName ?? ''}
            className='w-28 h-28 rounded-full object-cover mx-auto mb-3'
          />
        )}
        <h2 className='text-xl font-semibold mb-0'>{parent.name}</h2>
        <div>
          {parent.address?.city}
          {parent.address?.neighborhood && ' - '}
          {parent.address?.neighborhood}
        </div>
        {kidsCount > 0 && (
          <div className='mb-3'>
            {kidsCount} {kidsCount === 1 ? 'dependent' : 'dependents'}
            {viewerIsSitter && parent.userStats?.lastAccessLabel && (
              <span className='ml-2 lowercase'>
                - <i className='fa-solid fa-bolt mr-1' />
                active {parent.userStats.lastAccessLabel}
              </span>
            )}
          </div>
        )}
        {viewerIsSitter && (
          <div className='inline-flex gap-3 mb-4'>
            <label className='cursor-pointer'>
              <input
                type='radio'
                name='availability-share'
                checked={!blocked}
                onChange={(): void => onBlockToggle(false)}
                className='mr-1'
              />
              Share Availability
            </label>
            <label className='cursor-pointer'>
              <input
                type='radio'
                name='availability-share'
                checked={blocked}
                onChange={(): void => onBlockToggle(true)}
                className='mr-1'
              />
              {t('pages.parentProfile.buttons.hideAvailability')}
            </label>
            <i
              className='fa-solid fa-circle-info align-middle ml-1'
              title={t('pages.parentProfile.tooltips.hideAvailability')}
            />
          </div>
        )}
      </div>
      <div className='md:col-span-3 text-right'>
        {!canEdit && (
          <Button
            buttonType={ButtonType.tertiary}
            id='flag-user'
            onClick={onFlag}
            className='mr-2'
            title={t('pages.parentProfile.aria.report')}
          >
            <FontAwesomeIcon icon={faFlag} className='text-xl' />
          </Button>
        )}
        <Button buttonType={ButtonType.secondary} onClick={onShare}>
          <i className='fa-solid fa-share mr-1' /> {t('common.share')}
        </Button>
      </div>
    </div>
  );
};

// -- ParentSittersPreview --------------------------------------------------
const ParentSittersPreview = ({
  sitters,
  parentId,
  canEdit,
}: {
  sitters: Sitter[];
  parentId: number;
  canEdit: boolean;
}): JSX.Element | null => {
  if (!sitters.length) return null;
  const label = canEdit ? 'My' : `${sitters[0]?.firstName ? '' : ''}`;
  const prefix = canEdit ? 'My' : '';
  return (
    <a
      href={`/app/parent/${parentId}/booked-sitters`}
      className='block border rounded p-4 mb-3 hover:bg-neutral-50'
    >
      <div className='flex justify-between items-center'>
        <h2 className='text-lg font-semibold'>
          {prefix || label} Booked Caregivers
        </h2>
        <span className='btn btn-secondary'>See All</span>
      </div>
      <div className='flex gap-2 mt-2'>
        {sitters.map((s) => (
          <img
            key={s.id}
            src={s.pictureUrl}
            alt={`booked-sitter-preview-${s.id}`}
            className='w-8 h-8 rounded-full object-cover'
          />
        ))}
      </div>
    </a>
  );
};

// -- ParentOpenJobs --------------------------------------------------------
const ParentOpenJobs = ({ jobs, parent }: { jobs: Job[]; parent: ParentExtras }): JSX.Element | null => {
  if (!jobs.length) return null;
  return (
    <div className='border-t border-b py-4 mb-4'>
      <h2 className='text-xl font-semibold mb-3'>Open Job Posts</h2>
      <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3'>
        {jobs.map((job) => {
          const booking = job.booking;
          const title = getJobTitle(job);
          const children = getChildrenLine(booking);
          const description = booking?.description?.slice(0, 36);
          const truncated = (booking?.description?.length ?? 0) > 36 ? '...' : '';
          return (
            <a
              key={job.id}
              href={`/app/job/${job.id}`}
              className='block us-card rounded p-3 hover:bg-neutral-50 flex gap-3'
            >
              {parent.pictureUrl && (
                <img
                  src={parent.pictureUrl}
                  alt={parent.firstName ?? ''}
                  className='w-16 h-16 rounded-full object-cover flex-shrink-0'
                />
              )}
              <div className='flex-1 min-w-0'>
                <div className='font-semibold mb-1'>{title || 'Job'}</div>
                {booking?.rate ? (
                  <div className='text-sm text-neutral-600'>
                    ${booking.rate}/hr
                    {booking.paymentType && (
                      <span> via {booking.paymentType}</span>
                    )}
                  </div>
                ) : null}
                {booking?.address?.city && (
                  <div className='text-sm text-neutral-600'>{booking.address.city}</div>
                )}
                {children && (
                  <div className='text-sm text-neutral-600'>{children}</div>
                )}
                {description && (
                  <div className='text-sm text-neutral-600 mt-1'>
                    {description}
                    {truncated}
                  </div>
                )}
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
};

// -- ParentBadgesGroups ----------------------------------------------------
interface GroupItem {
  id?: number;
  name?: string;
  alias?: string;
}
const ParentBadgesGroups = ({ parent }: { parent: ParentExtras }): JSX.Element => {
  const badges = parent.badges ?? [];
  const local: GroupItem[] = (parent.localGroups ?? []).map((g) => ({
    id: g.id,
    name: g.localGroup?.name,
    alias: (g.localGroup as { alias?: string } | undefined)?.alias,
  }));
  const affiliations: GroupItem[] = (parent.affiliations ?? []).map((g) => ({
    id: g.id,
    name: g.affiliation?.name,
    alias: (g.affiliation as { alias?: string } | undefined)?.alias,
  }));
  const schools: GroupItem[] = (parent.schools ?? []).map((g) => ({
    id: g.id,
    name: g.school?.name,
    alias: (g.school as { alias?: string } | undefined)?.alias,
  }));
  const GroupList = ({
    label,
    items,
    pathSegment,
  }: {
    label: string;
    items: GroupItem[];
    pathSegment: 'local-groups' | 'affiliations' | 'schools';
  }): JSX.Element | null => {
    if (!items.length) return null;
    return (
      <div className='mb-2'>
        <span className='font-semibold mr-1'>{label}:</span>
        {items.map((g, i) => {
          const sep = i < items.length - 1 ? ', ' : '';
          if (g.alias) {
            return (
              <span key={g.id ?? i}>
                <a
                  href={`/app/sitters/${pathSegment}/${g.alias}`}
                  className='text-sapphire hover:underline'
                >
                  {g.name}
                </a>
                {sep}
              </span>
            );
          }
          return (
            <span key={g.id ?? i}>
              {g.name}
              {sep}
            </span>
          );
        })}
      </div>
    );
  };
  return (
    <div className='grid grid-cols-1 md:grid-cols-2 border-t border-b py-4 mb-4'>
      <div className='py-2 md:pr-4'>
        <h3 className='font-semibold mb-1'>Badges</h3>
        {badges.length === 0 && <p className='text-neutral-500'>No badges yet.</p>}
        {badges.map((badge, i) => {
          const iconClass = badge.type ? BADGE_ICON[badge.type] ?? 'fa-solid fa-award' : 'fa-solid fa-award';
          const activeStyle = badge.active === false ? 'text-neutral-400' : 'text-sapphire';
          const onClick = (): void => {
            if (badge.url) window.open(badge.url, '_blank');
          };
          return (
            <div
              key={`${badge.type}-${i}`}
              className={`mb-2 ${badge.url ? 'cursor-pointer' : ''}`}
              onClick={onClick}
              onKeyDown={(e): void => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick();
                }
              }}
              role={badge.url ? 'button' : undefined}
              tabIndex={badge.url ? 0 : undefined}
              title={badge.description ?? badge.label}
            >
              <i className={`${iconClass} ${activeStyle} mr-2 text-xl`} />
              <span className={badge.active === false ? 'text-neutral-400' : ''}>{badge.label}</span>
            </div>
          );
        })}
      </div>
      <div className='py-2 md:pl-4'>
        <h3 className='font-semibold mb-1'>Groups</h3>
        {local.length + affiliations.length + schools.length === 0 && (
          <p className='text-neutral-500'>No groups yet.</p>
        )}
        <GroupList label='Local Groups' items={local} pathSegment='local-groups' />
        <GroupList label='Affiliations' items={affiliations} pathSegment='affiliations' />
        <GroupList label='Schools' items={schools} pathSegment='schools' />
      </div>
    </div>
  );
};

// -- ParentProfileFeedback --------------------------------------------------
const ParentProfileFeedback = ({
  parent,
  viewerIsParentSelf,
  viewerIsSitter,
  onSeeMore,
}: {
  parent: ParentExtras;
  viewerIsParentSelf: boolean;
  viewerIsSitter: boolean;
  onSeeMore: () => void;
}): JSX.Element => {
  const rec = parent.recommendation ?? {};
  const count = rec.reviewCount ?? 0;
  return (
    <div className='grid grid-cols-1 md:grid-cols-12 border-t border-b py-4 mb-4 gap-3'>
      <div id='sitter-feedback' className='md:col-span-10'>
        <h3 className='font-semibold mb-2'>Caregiver Feedback</h3>
        {count === 0 && (
          <div className='text-neutral-500 flex items-center gap-2'>
            <FontAwesomeIcon icon={faThumbsUp} className='text-3xl' />
            No recommendations yet
          </div>
        )}
        {count > 0 && (
          <div className='flex items-start gap-2'>
            <FontAwesomeIcon icon={faThumbsUp} className='text-3xl' />
            <div>
              {rec.rating}% of caregivers recommend {parent.firstName}
              <br />
              <span className='italic text-sm'>
                Based on feedback from {count} caregiver{count === 1 ? '' : 's'}
              </span>
            </div>
          </div>
        )}
      </div>
      <div className='md:col-span-2 text-right'>
        {viewerIsParentSelf && (
          <Button buttonType={ButtonType.secondary} onClick={onSeeMore}>
            See More
          </Button>
        )}
        {viewerIsSitter && (
          <a
            href={`/app/parent/${parent.id}/reviews`}
            className='font-semibold block mt-2 md:mt-10'
          >
            See more
          </a>
        )}
      </div>
    </div>
  );
};

// -- ParentDetail (owner / booked viewer) ----------------------------------
const ParentDetail = ({
  parent,
  canEdit,
  onEditSection,
}: {
  parent: ParentExtras;
  canEdit: boolean;
  onEditSection: (section: string) => void;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [expanded, setExpanded] = useState(true);
  const kids = parent.kids ?? [];
  const languages = parent.languages ?? [];
  const address = parent.address as Partial<{
    street: string;
    street2: string;
    city: string;
    state: string;
    zip: string;
    neighborhood: string;
  }> | undefined;
  const EditBtn = ({ section }: { section: string }): JSX.Element | null =>
    canEdit ? (
      <Button
        buttonType={ButtonType.secondary}
        onClick={(): void => onEditSection(section)}
      >
        Edit
      </Button>
    ) : null;

  return (
    <>
      {canEdit && (
        <div className='border-t border-b py-4 mb-4 grid grid-cols-1 md:grid-cols-12 gap-3'>
          <div id='vaccine-status' className='md:col-span-10'>
            <h3 className='font-semibold mb-1'>COVID-19 Vaccine</h3>
            {parent.covidBadgeDisplay ? (
              <div>
                <i className='fa-solid fa-syringe mr-2 text-sapphire' />
                One or more adults in my household are willing to provide proof of COVID-19 vaccine
              </div>
            ) : (
              <div>
                <i className='fa-solid fa-syringe mr-2 text-neutral-400' />
                No adults in my household have received or are willing to provide proof of COVID-19 vaccine
              </div>
            )}
          </div>
          <div className='md:col-span-2 text-right'>
            <EditBtn section='vaccine' />
          </div>
        </div>
      )}

      <div id='parent-profile-details' className='border-t border-b py-4 mb-4'>
        <div className='grid grid-cols-1 md:grid-cols-12 mb-2'>
          <h3 className='md:col-span-10 font-semibold mb-0'>
            <i className='fa-solid fa-lock mr-2' />
            Family Details
          </h3>
          <div className='md:col-span-2 text-right'>
            <Button
              buttonType={ButtonType.tertiary}
              onClick={(): void => setExpanded((v) => !v)}
            >
              <FontAwesomeIcon
                icon={expanded ? faChevronUp : faChevronDown}
                className='align-middle'
              />
            </Button>
          </div>
          {canEdit && (
            <div className='md:col-span-10 text-sm text-neutral-600'>
              These details are only shared with caregivers you choose to book
            </div>
          )}
        </div>

        {expanded && (
          <div className='space-y-4'>
            {(canEdit || kids.length > 0) && (
              <>
                <hr />
                <div className='grid grid-cols-1 md:grid-cols-12 gap-3'>
                  <div id='children' className='md:col-span-10'>
                    <h4 className='font-semibold mb-1'>Dependents</h4>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                      {kids.map((kid, i) => (
                        <div key={i}>
                          <div className='font-semibold'>{kid.name}</div>
                          <div>Age: {kid.age}</div>
                          {kid.allergies && <div>Allergies: {kid.allergies}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className='md:col-span-2 text-right'>
                    <EditBtn section='children' />
                  </div>
                </div>
              </>
            )}

            {canEdit && (
              <>
                <hr />
                <div className='grid grid-cols-1 md:grid-cols-12 gap-3'>
                  <div id='home-address' className='md:col-span-10'>
                    <h4 className='font-semibold mb-1'>Home Address</h4>
                    <div>{address?.street}</div>
                    {address?.street2 && <div>{address.street2}</div>}
                    <div>
                      {address?.city}
                      {address?.city && ', '}
                      {address?.state} {address?.zip}
                    </div>
                    {address?.neighborhood && (
                      <div>Neighborhood: {address.neighborhood}</div>
                    )}
                  </div>
                  <div className='md:col-span-2 text-right'>
                    <EditBtn section='address' />
                  </div>
                </div>
              </>
            )}

            <hr />
            <div className='grid grid-cols-1 md:grid-cols-12 gap-3'>
              <div id='parking' className='md:col-span-10'>
                <h4 className='font-semibold mb-1'>Parking</h4>
                <p>
                  Is there ample parking in your area?{' '}
                  <b>{parent.hasParkingArea ? 'Yes' : 'No'}</b>
                </p>
                <p>
                  Do you have a parking spot that the caregiver can use?{' '}
                  <b>{parent.hasSitterParking ? 'Yes' : 'No'}</b>
                </p>
              </div>
              <div className='md:col-span-2 text-right'>
                <EditBtn section='parking' />
              </div>
            </div>

            <hr />
            <div className='grid grid-cols-1 md:grid-cols-12 gap-3'>
              <div id='emergency-contacts' className='md:col-span-10'>
                <h4 className='font-semibold mb-1'>Emergency Contacts</h4>
                {(parent.emergencyName || parent.emergencyPhone) && (
                  <div>
                    Emergency contact: {parent.emergencyName}
                    {parent.emergencyName && parent.emergencyPhone && ', '}
                    {parent.emergencyPhone}
                  </div>
                )}
                {(parent.pediatricianName || parent.pediatricianPhone) && (
                  <div>
                    Pediatrician: {parent.pediatricianName}
                    {parent.pediatricianName && parent.pediatricianPhone && ', '}
                    {parent.pediatricianPhone}
                  </div>
                )}
              </div>
              <div className='md:col-span-2 text-right'>
                <EditBtn section='contacts' />
              </div>
            </div>

            <hr />
            <div className='grid grid-cols-1 md:grid-cols-12 gap-3'>
              <div id='languages' className='md:col-span-10'>
                <h4 className='font-semibold mb-1'>Languages Spoken</h4>
                <div>
                  {languages.length === 0 && (
                    <span className='text-neutral-500'>None specified</span>
                  )}
                  {languages.map((lang, i) => (
                    <span key={lang.id ?? i}>
                      {lang.name}
                      {i < languages.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>
              </div>
              <div className='md:col-span-2 text-right'>
                <EditBtn section='languages' />
              </div>
            </div>

            {(canEdit || (parent.schools ?? []).length > 0) && (
              <>
                <hr />
                <div className='grid grid-cols-1 md:grid-cols-12 gap-3'>
                  <div id='kids-schools' className='md:col-span-10'>
                    <h4 className='font-semibold mb-1'>Schools</h4>
                    <div>
                      {(parent.schools ?? []).map((s, i) => (
                        <span key={s.id ?? i}>
                          {s.school?.name}
                          {i < (parent.schools ?? []).length - 1 ? ', ' : ''}
                        </span>
                      ))}
                      {(parent.schools ?? []).length === 0 && (
                        <span className='text-neutral-500'>None yet</span>
                      )}
                    </div>
                  </div>
                  <div className='md:col-span-2 text-right'>
                    <EditBtn section='schools' />
                  </div>
                </div>
              </>
            )}

            <hr />
            <div className='grid grid-cols-1 md:grid-cols-12 gap-3'>
              <div id='groups' className='md:col-span-10'>
                <h4 className='font-semibold mb-1'>Groups</h4>
                {(parent.localGroups ?? []).length + (parent.affiliations ?? []).length === 0 && (
                  <span className='text-neutral-500'>None yet</span>
                )}
                {(parent.localGroups ?? []).map((g, i) => (
                  <span key={g.id ?? i}>
                    {g.localGroup?.name}
                    {i < (parent.localGroups ?? []).length - 1 ? ', ' : ''}
                  </span>
                ))}
                {(parent.affiliations ?? []).length > 0 && (
                  <>
                    <br />
                    {(parent.affiliations ?? []).map((g, i) => (
                      <span key={g.id ?? i}>
                        {g.affiliation?.name}
                        {i < (parent.affiliations ?? []).length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </>
                )}
              </div>
              <div className='md:col-span-2 text-right'>
                <EditBtn section='groups' />
              </div>
            </div>

            <hr />
            <div className='grid grid-cols-1 md:grid-cols-12 gap-3'>
              <div id='pets' className='md:col-span-10'>
                <h4 className='font-semibold mb-1'>{t('pages.parentProfile.sections.pets')}</h4>
                <div className='flex gap-4'>
                  <span>
                    <FontAwesomeIcon icon={faDog} className='mr-1' />
                    {parent.dog != null
                      ? t(parent.dog ? 'common.pets.hasDogsCount' : 'common.pets.noDogsCount')
                      : t('common.pets.notSpecifiedDogs')}
                  </span>
                  <span>
                    <FontAwesomeIcon icon={faCat} className='mr-1' />
                    {parent.cat != null
                      ? t(parent.cat ? 'common.pets.hasCatsCount' : 'common.pets.noCatsCount')
                      : t('common.pets.notSpecifiedCats')}
                  </span>
                  <span>
                    <FontAwesomeIcon icon={faPaw} className='mr-1' />
                    {parent.otherPet != null
                      ? t(parent.otherPet ? 'common.pets.hasOtherPetsCount' : 'common.pets.noOtherPetsCount')
                      : t('common.pets.notSpecifiedOtherPets')}
                  </span>
                </div>
                {parent.petNote && (
                  <p className='mt-2'>
                    <b>{t('pages.parentProfile.sections.notesLabel')} </b>
                    {parent.petNote}
                  </p>
                )}
              </div>
              <div className='md:col-span-2 text-right'>
                <EditBtn section='pets' />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// -- Edit Section Modal ----------------------------------------------------
type EditSection =
  | 'vaccine'
  | 'children'
  | 'address'
  | 'parking'
  | 'contacts'
  | 'languages'
  | 'pets'
  | 'schools'
  | 'groups'
  | null;

interface KidForm {
  id?: number;
  name: string;
  year: string;
  month: string;
  allergies: string;
  experienceIds: Set<number>;
}

const parseDob = (dob?: string): { year: string; month: string } => {
  if (!dob) return { year: '', month: '' };
  const m = dob.match(/^(\d{4})-(\d{2})/);
  if (m) return { year: m[1], month: String(Number(m[2])) };
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return { year: '', month: '' };
  return { year: String(d.getFullYear()), month: String(d.getMonth() + 1) };
};

const buildDob = (year: string, month: string): string | undefined => {
  const y = Number(year);
  const m = Number(month);
  if (!y || y < 1900) return undefined;
  const mm = m && m >= 1 && m <= 12 ? m : 1;
  return `${y}-${String(mm).padStart(2, '0')}-01`;
};

// Minimal typeahead matching ExploreLists pattern — debounced search input
// with a dropdown; clicking a row fires onSelect and clears the query.
const AssociationTypeahead = ({
  label,
  placeholder,
  search,
  onSelect,
}: {
  label: string;
  placeholder: string;
  search: (q: string) => Promise<Array<{ id?: number; name?: string }>>;
  onSelect: (item: { id?: number; name?: string }) => Promise<void>;
}): JSX.Element => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Array<{ id?: number; name?: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      void (async (): Promise<void> => {
        try {
          const list = await search(query.trim());
          setResults(list);
        } catch {
          setResults([]);
        } finally {
          setLoading(false);
        }
      })();
    }, 250);
    return (): void => clearTimeout(timer);
  }, [query, search]);

  const handleSelect = async (item: { id?: number; name?: string }): Promise<void> => {
    setSubmitting(true);
    try {
      await onSelect(item);
      setQuery('');
      setResults([]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='mt-3 mb-3'>
      <label className='block font-semibold mb-1 text-sm'>{label}</label>
      <div className='relative'>
        <input
          type='text'
          className='form-control w-full border border-neutral-300 rounded px-3 py-2'
          placeholder={placeholder}
          value={query}
          onChange={(e): void => setQuery(e.target.value)}
          disabled={submitting}
        />
        {query.trim().length >= 2 && (
          <div className='absolute z-10 left-0 right-0 bg-white border border-neutral-200 rounded shadow-sm mt-1 max-h-56 overflow-auto'>
            {loading && <div className='px-3 py-2 text-sm text-neutral-500'>Searching…</div>}
            {!loading && results.length === 0 && (
              <div className='px-3 py-2 text-sm text-neutral-500'>No matches.</div>
            )}
            {!loading &&
              results.map((r) => (
                <Button
                  key={r.id}
                  buttonType={ButtonType.tertiary}
                  className='w-full text-left px-3 py-2 hover:bg-neutral-50'
                  disabled={submitting}
                  onClick={(): Promise<void> => handleSelect(r)}
                >
                  {r.name}
                </Button>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

const EditSectionModal = ({
  section,
  parent,
  languages,
  onClose,
  onSave,
  onRemoveAssociation,
  onAddAssociation,
}: {
  section: EditSection;
  parent: ParentExtras;
  languages: Language[];
  onClose: () => void;
  onSave: (patch: Partial<ParentExtras>) => Promise<void>;
  onRemoveAssociation: (
    kind: 'school' | 'affiliation' | 'localGroup',
    id: number,
  ) => Promise<void>;
  onAddAssociation: (
    kind: 'school' | 'affiliation' | 'localGroup',
    entityId: number,
  ) => Promise<void>;
}): JSX.Element | null => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [covidReceived, setCovidReceived] = useState(!!parent.covidBadgeDisplay);
  const [covidDisplay, setCovidDisplay] = useState(!!parent.covidBadgeDisplay);

  const [kidsForms, setKidsForms] = useState<KidForm[]>(() =>
    (parent.kids ?? []).map((k) => {
      const dob = parseDob((k as { dob?: string }).dob);
      const experienceIds = new Set<number>();
      ((k as { experiences?: Array<{ specialExperience?: { id?: number } }> }).experiences ?? [])
        .forEach((e) => {
          if (e.specialExperience?.id) experienceIds.add(e.specialExperience.id);
        });
      return {
        id: k.id,
        name: k.name ?? '',
        year: dob.year,
        month: dob.month,
        allergies: k.allergies ?? '',
        experienceIds,
      };
    }),
  );

  const [specialExperiences, setSpecialExperiences] = useState<SpecialExperience[]>([]);
  useEffect(() => {
    if (section !== 'children') return;
    const load = async (): Promise<void> => {
      try {
        const r = await SpecialExperiencesService.getByFilter();
        setSpecialExperiences(r.data?.data ?? []);
      } catch {
        setSpecialExperiences([]);
      }
    };
    void load();
  }, [section]);

  const [addr, setAddr] = useState(() => ({
    street: parent.address?.street ?? '',
    street2: parent.address?.street2 ?? '',
    city: parent.address?.city ?? '',
    state: parent.address?.state ?? '',
    zip: parent.address?.zip ?? '',
    neighborhood: parent.address?.neighborhood ?? '',
  }));

  const [hasParkingArea, setHasParkingArea] = useState(!!parent.hasParkingArea);
  const [hasSitterParking, setHasSitterParking] = useState(!!parent.hasSitterParking);

  const [emergencyName, setEmergencyName] = useState(parent.emergencyName ?? '');
  const [emergencyPhone, setEmergencyPhone] = useState(parent.emergencyPhone ?? '');
  const [pediatricianName, setPediatricianName] = useState(parent.pediatricianName ?? '');
  const [pediatricianPhone, setPediatricianPhone] = useState(parent.pediatricianPhone ?? '');

  // selectedLanguageIds tracks LANGUAGE IDs (not parent-language assoc IDs).
  const [selectedLanguageIds, setSelectedLanguageIds] = useState<Set<number>>(
    () => new Set(
      (parent.languages ?? [])
        .map((l) => l.languageId ?? l.id)
        .filter((n): n is number => !!n),
    ),
  );

  const [dog, setDog] = useState<boolean | null>(parent.dog ?? null);
  const [cat, setCat] = useState<boolean | null>(parent.cat ?? null);
  const [otherPet, setOtherPet] = useState<boolean | null>(parent.otherPet ?? null);
  const [petNote, setPetNote] = useState(parent.petNote ?? '');

  if (!section) return null;

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    setError('');
    try {
      let patch: Partial<ParentExtras> = {};
      if (section === 'vaccine') {
        patch = { covidBadgeDisplay: covidReceived && covidDisplay };
      } else if (section === 'children') {
        patch = {
          kids: kidsForms.map((f) => {
            const dob = buildDob(f.year, f.month);
            const experiences = Array.from(f.experienceIds).map((expId) => ({
              specialExperience: { id: expId },
            }));
            return {
              id: f.id,
              name: f.name,
              dob,
              allergies: f.allergies || undefined,
              experiences,
            } as unknown as NonNullable<ParentExtras['kids']>[number];
          }) as ParentExtras['kids'],
        };
      } else if (section === 'address') {
        patch = {
          address: {
            street: addr.street,
            street2: addr.street2,
            city: addr.city,
            state: addr.state.toUpperCase(),
            zip: addr.zip,
            neighborhood: addr.neighborhood,
          },
        };
      } else if (section === 'parking') {
        patch = { hasParkingArea, hasSitterParking };
      } else if (section === 'contacts') {
        patch = { emergencyName, emergencyPhone, pediatricianName, pediatricianPhone };
      } else if (section === 'languages') {
        // Mirror Angular parent-detail.updateLanguages — diff the selected set
        // against existing parent-language associations and call the
        // /v2/parent-languages endpoint per add/remove. The Parent PATCH does
        // NOT update languages, which is why the prior implementation didn't
        // persist or re-render.
        const existing = parent.languages ?? [];
        const existingByLangId = new Map<number, number>();
        for (const pl of existing) {
          const langId = pl.languageId ?? pl.id;
          if (langId && pl.id) existingByLangId.set(langId, pl.id);
        }
        const toAdd: number[] = [];
        const toRemove: number[] = [];
        for (const langId of selectedLanguageIds) {
          if (!existingByLangId.has(langId)) toAdd.push(langId);
        }
        for (const [langId, assocId] of existingByLangId.entries()) {
          if (!selectedLanguageIds.has(langId)) toRemove.push(assocId);
        }
        await Promise.all([
          ...toAdd.map((langId) =>
            ParentLanguagesService.create({ parentId: parent.id, languageId: langId }),
          ),
          ...toRemove.map((id) => ParentLanguagesService.delete(id)),
        ]);
        // Trigger refresh on the parent component.
        await onSave({});
        onClose();
        return;
      } else if (section === 'pets') {
        patch = { dog: dog ?? undefined, cat: cat ?? undefined, otherPet: otherPet ?? undefined, petNote };
      } else if (section === 'schools' || section === 'groups') {
        // Removals happen inline via onRemoveAssociation — nothing to save here.
        onClose();
        return;
      }
      await onSave(patch);
      onClose();
    } catch {
      setError("Couldn't save. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const titles: Record<Exclude<EditSection, null>, string> = {
    vaccine: t('pages.parentProfile.sectionTitles.vaccine'),
    children: t('pages.parentProfile.sectionTitles.children'),
    address: t('pages.parentProfile.sectionTitles.address'),
    parking: t('pages.parentProfile.sectionTitles.parking'),
    contacts: t('pages.parentProfile.sectionTitles.contacts'),
    languages: t('pages.parentProfile.sectionTitles.languages'),
    pets: t('pages.parentProfile.sectionTitles.pets'),
    schools: t('pages.parentProfile.sectionTitles.schools'),
    groups: t('pages.parentProfile.sectionTitles.groups'),
  };

  const cyclePet = (val: boolean | null): boolean | null => {
    if (val === null) return true;
    if (val === true) return false;
    return null;
  };

  const addKid = (): void => {
    setKidsForms((prev) => [
      ...prev,
      { name: '', year: '', month: '', allergies: '', experienceIds: new Set<number>() },
    ]);
  };
  const removeKid = (i: number): void => {
    setKidsForms((prev) => prev.filter((_, idx) => idx !== i));
  };
  const updateKid = (i: number, key: 'name' | 'year' | 'month' | 'allergies', val: string): void => {
    setKidsForms((prev) =>
      prev.map((k, idx) => (idx === i ? { ...k, [key]: val } : k)),
    );
  };
  const toggleKidExperience = (i: number, expId: number): void => {
    setKidsForms((prev) =>
      prev.map((k, idx) => {
        if (idx !== i) return k;
        const next = new Set(k.experienceIds);
        if (next.has(expId)) next.delete(expId);
        else next.add(expId);
        return { ...k, experienceIds: next };
      }),
    );
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={titles[section]}
      className='w-full max-w-2xl'
      footer={
        section === 'schools' || section === 'groups' ? (
          <Button buttonType={ButtonType.primary} onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button buttonType={ButtonType.tertiary} onClick={onClose}>
              Cancel
            </Button>
            <Button buttonType={ButtonType.primary} disabled={submitting} onClick={submit}>
              Save
            </Button>
          </>
        )
      }
    >
      {section === 'vaccine' && (
        <div className='space-y-2 mb-4'>
          <label className='flex items-start gap-2'>
            <input
              type='checkbox'
              checked={covidReceived}
              onChange={(e): void => setCovidReceived(e.target.checked)}
            />
            <span>
              I certify that I have received the COVID-19 vaccine and am willing to
              provide proof of a current COVID-19 vaccine, if requested by a caregiver
              on the UrbanSitter platform.
            </span>
          </label>
          <label className='flex items-start gap-2'>
            <input
              type='checkbox'
              checked={covidDisplay}
              onChange={(e): void => setCovidDisplay(e.target.checked)}
            />
            <span>
              I give consent for UrbanSitter to display that I am willing to provide
              proof of a current COVID-19 vaccine on my family profile.
            </span>
          </label>
        </div>
      )}

      {section === 'children' && (
        <div>
          {kidsForms.length === 0 && (
            <p className='text-neutral-600 mb-3'>
              {t('pages.parentProfile.editKid.emptyState')}
            </p>
          )}
          {kidsForms.map((kid, i) => (
            <div key={i} className='border border-neutral-200 rounded p-3 mb-3'>
              <div className='flex justify-between items-start mb-2'>
                <h4 className='font-semibold'>{t('pages.parentProfile.editKid.dependent', { number: i + 1 })}</h4>
                <Button
                  buttonType={ButtonType.tertiary}
                  className='text-sm underline text-red-600'
                  onClick={(): void => removeKid(i)}
                >
                  {t('common.remove')}
                </Button>
              </div>
              <input
                type='text'
                className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-2'
                placeholder={t('common.name')}
                value={kid.name}
                onChange={(e): void => updateKid(i, 'name', e.target.value)}
              />
              <div className='grid grid-cols-2 gap-2 mb-2'>
                <input
                  type='number'
                  min={1}
                  max={12}
                  className='form-control w-full border border-neutral-300 rounded px-3 py-2'
                  placeholder={t('pages.parentProfile.placeholders.birthMonth')}
                  value={kid.month}
                  onChange={(e): void => updateKid(i, 'month', e.target.value)}
                />
                <input
                  type='number'
                  className='form-control w-full border border-neutral-300 rounded px-3 py-2'
                  placeholder={t('pages.parentProfile.placeholders.birthYear')}
                  value={kid.year}
                  onChange={(e): void => updateKid(i, 'year', e.target.value)}
                />
              </div>
              <input
                type='text'
                className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-3'
                placeholder={t('pages.parentProfile.placeholders.allergies')}
                value={kid.allergies}
                onChange={(e): void => updateKid(i, 'allergies', e.target.value)}
              />
              {specialExperiences.length > 0 && (
                <div>
                  <h5 className='font-semibold mb-2'>{t('pages.parentProfile.editKid.additionalConsiderations')}</h5>
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-1'>
                    {specialExperiences.map((exp) => (
                      <label
                        key={exp.id}
                        className='flex items-center gap-2 text-sm cursor-pointer'
                      >
                        <input
                          type='checkbox'
                          checked={!!exp.id && kid.experienceIds.has(exp.id)}
                          onChange={(): void => {
                            if (exp.id) toggleKidExperience(i, exp.id);
                          }}
                        />
                        <span>{exp.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          <Button
            buttonType={ButtonType.secondary}
            className='mb-3'
            onClick={addKid}
          >
            {t('pages.parentProfile.editKid.addAnother')}
          </Button>
        </div>
      )}

      {section === 'address' && (
        <div>
          <div className='grid grid-cols-12 gap-2 mb-2'>
            <input
              type='text'
              className='col-span-7 form-control border border-neutral-300 rounded px-3 py-2'
              placeholder={t('pages.parentProfile.placeholders.street')}
              value={addr.street}
              onChange={(e): void => setAddr((a) => ({ ...a, street: e.target.value }))}
            />
            <input
              type='text'
              className='col-span-5 form-control border border-neutral-300 rounded px-3 py-2'
              placeholder={t('pages.parentProfile.placeholders.apt')}
              value={addr.street2}
              onChange={(e): void => setAddr((a) => ({ ...a, street2: e.target.value }))}
            />
          </div>
          <div className='grid grid-cols-12 gap-2 mb-2'>
            <input
              type='text'
              className='col-span-7 form-control border border-neutral-300 rounded px-3 py-2'
              placeholder={t('pages.parentProfile.placeholders.city')}
              value={addr.city}
              onChange={(e): void => setAddr((a) => ({ ...a, city: e.target.value }))}
            />
            <input
              type='text'
              className='col-span-5 form-control border border-neutral-300 rounded px-3 py-2'
              placeholder={t('pages.parentProfile.placeholders.state')}
              maxLength={2}
              value={addr.state}
              onChange={(e): void => setAddr((a) => ({ ...a, state: e.target.value }))}
            />
          </div>
          <div className='grid grid-cols-12 gap-2 mb-2'>
            <input
              type='text'
              className='col-span-4 form-control border border-neutral-300 rounded px-3 py-2'
              placeholder={t('pages.parentProfile.placeholders.zip')}
              maxLength={5}
              value={addr.zip}
              onChange={(e): void => setAddr((a) => ({ ...a, zip: e.target.value }))}
            />
            <input
              type='text'
              className='col-span-8 form-control border border-neutral-300 rounded px-3 py-2'
              placeholder={t('pages.parentProfile.placeholders.neighborhood')}
              value={addr.neighborhood}
              onChange={(e): void => setAddr((a) => ({ ...a, neighborhood: e.target.value }))}
            />
          </div>
        </div>
      )}

      {section === 'parking' && (
        <div className='space-y-2 mb-4'>
          <label className='flex items-start gap-2'>
            <input
              type='checkbox'
              checked={hasParkingArea}
              onChange={(e): void => setHasParkingArea(e.target.checked)}
            />
            {t('pages.parentProfile.parking.hasArea')}
          </label>
          <label className='flex items-start gap-2'>
            <input
              type='checkbox'
              checked={hasSitterParking}
              onChange={(e): void => setHasSitterParking(e.target.checked)}
            />
            {t('pages.parentProfile.parking.hasSpot')}
          </label>
        </div>
      )}

      {section === 'contacts' && (
        <div className='space-y-3 mb-4'>
          <div>
            <h4 className='font-semibold mb-1'>{t('pages.parentProfile.contacts.emergencyHeading')}</h4>
            <input
              type='text'
              className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-2'
              placeholder={t('common.name')}
              value={emergencyName}
              onChange={(e): void => setEmergencyName(e.target.value)}
            />
            <input
              type='tel'
              maxLength={12}
              className='form-control w-full border border-neutral-300 rounded px-3 py-2'
              placeholder={t('pages.parentProfile.placeholders.phone')}
              value={emergencyPhone}
              onChange={(e): void => setEmergencyPhone(e.target.value)}
            />
          </div>
          <div>
            <h4 className='font-semibold mb-1'>{t('pages.parentProfile.contacts.pediatricianHeading')}</h4>
            <input
              type='text'
              className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-2'
              placeholder={t('common.name')}
              value={pediatricianName}
              onChange={(e): void => setPediatricianName(e.target.value)}
            />
            <input
              type='tel'
              maxLength={12}
              className='form-control w-full border border-neutral-300 rounded px-3 py-2'
              placeholder={t('pages.parentProfile.placeholders.phone')}
              value={pediatricianPhone}
              onChange={(e): void => setPediatricianPhone(e.target.value)}
            />
          </div>
        </div>
      )}

      {section === 'languages' && (
        <div className='space-y-1 mb-4 max-h-[50vh] overflow-auto'>
          {languages.map((lang) => (
            <label key={lang.id} className='flex items-center gap-2'>
              <input
                type='checkbox'
                checked={lang.id ? selectedLanguageIds.has(lang.id) : false}
                onChange={(): void => {
                  setSelectedLanguageIds((prev) => {
                    const next = new Set(prev);
                    if (lang.id) {
                      if (next.has(lang.id)) next.delete(lang.id);
                      else next.add(lang.id);
                    }
                    return next;
                  });
                }}
              />
              {lang.name}
            </label>
          ))}
        </div>
      )}

      {section === 'pets' && (
        <div className='mb-4'>
          <p className='text-sm mb-3'>Click to cycle: Yes → No → Unset</p>
          <div className='flex gap-4 mb-3'>
            <Button
              buttonType={ButtonType.secondary}
              className={`px-4 py-2 rounded border ${
                dog === true ? 'bg-sapphire-50 border-sapphire' : dog === false ? 'bg-neutral-100 border-neutral-400' : 'border-neutral-300'
              }`}
              onClick={(): void => setDog(cyclePet(dog))}
            >
              <i className='fa-solid fa-dog mr-2' />
              {dog === null
                ? t('common.pets.dogPrompt')
                : dog
                ? t('common.pets.hasDogs')
                : t('common.pets.noDogs')}
            </Button>
            <Button
              buttonType={ButtonType.secondary}
              className={`px-4 py-2 rounded border ${
                cat === true ? 'bg-sapphire-50 border-sapphire' : cat === false ? 'bg-neutral-100 border-neutral-400' : 'border-neutral-300'
              }`}
              onClick={(): void => setCat(cyclePet(cat))}
            >
              <i className='fa-solid fa-cat mr-2' />
              {cat === null
                ? t('common.pets.catPrompt')
                : cat
                ? t('common.pets.hasCats')
                : t('common.pets.noCats')}
            </Button>
            <Button
              buttonType={ButtonType.secondary}
              className={`px-4 py-2 rounded border ${
                otherPet === true ? 'bg-sapphire-50 border-sapphire' : otherPet === false ? 'bg-neutral-100 border-neutral-400' : 'border-neutral-300'
              }`}
              onClick={(): void => setOtherPet(cyclePet(otherPet))}
            >
              <i className='fa-solid fa-paw mr-2' />
              {otherPet === null
                ? t('common.pets.otherPetPrompt')
                : otherPet
                ? t('common.pets.hasOtherPets')
                : t('common.pets.noOtherPets')}
            </Button>
          </div>
          <textarea
            className='form-control w-full border border-neutral-300 rounded p-2'
            rows={3}
            placeholder={t('pages.parentProfile.placeholders.petDetails')}
            value={petNote}
            onChange={(e): void => setPetNote(e.target.value)}
          />
        </div>
      )}

      {section === 'schools' && (
        <div className='mb-4'>
          {(parent.schools ?? []).length === 0 && (
            <p className='text-neutral-500 mb-2'>You aren&apos;t associated with any schools yet.</p>
          )}
          {(parent.schools ?? []).map((s) => (
            <div
              key={s.id}
              className='flex items-center justify-between border border-neutral-200 rounded px-3 py-2 mb-2'
            >
              <span>{s.school?.name}</span>
              <Button
                buttonType={ButtonType.tertiary}
                aria-label={`Remove ${s.school?.name ?? 'school'}`}
                className='text-red-600 text-xl leading-none'
                onClick={(): Promise<void> =>
                  s.id != null ? onRemoveAssociation('school', s.id) : Promise.resolve()
                }
              >
                &times;
              </Button>
            </div>
          ))}
          <AssociationTypeahead
            label='Add a school'
            placeholder={t('pages.parentProfile.placeholders.searchSchool')}
            search={async (q): Promise<Array<{ id?: number; name?: string }>> => {
              const resp = await SchoolService.getByFilter({ params: { name: q } });
              return (resp.data?.data ?? []) as Array<{ id?: number; name?: string }>;
            }}
            onSelect={(item): Promise<void> =>
              item.id != null ? onAddAssociation('school', item.id) : Promise.resolve()
            }
          />
        </div>
      )}

      {section === 'groups' && (
        <div className='mb-4'>
          {(parent.affiliations ?? []).length + (parent.localGroups ?? []).length === 0 && (
            <p className='text-neutral-500 mb-2'>You aren&apos;t in any groups yet.</p>
          )}
          {(parent.affiliations ?? []).length > 0 && (
            <div className='mb-3'>
              <h4 className='font-semibold mb-1'>Affiliations</h4>
              {(parent.affiliations ?? []).map((a) => (
                <div
                  key={a.id}
                  className='flex items-center justify-between border border-neutral-200 rounded px-3 py-2 mb-2'
                >
                  <span>{a.affiliation?.name}</span>
                  <Button
                    buttonType={ButtonType.tertiary}
                    aria-label={`Remove ${a.affiliation?.name ?? 'affiliation'}`}
                    className='text-red-600 text-xl leading-none'
                    onClick={(): Promise<void> =>
                      a.id != null ? onRemoveAssociation('affiliation', a.id) : Promise.resolve()
                    }
                  >
                    &times;
                  </Button>
                </div>
              ))}
            </div>
          )}
          <AssociationTypeahead
            label='Add an affiliation'
            placeholder={t('pages.parentProfile.placeholders.searchAffiliations')}
            search={async (q): Promise<Array<{ id?: number; name?: string }>> => {
              const resp = await AffiliationService.getByFilter({ params: { name: q } });
              return (resp.data?.data ?? []) as Array<{ id?: number; name?: string }>;
            }}
            onSelect={(item): Promise<void> =>
              item.id != null ? onAddAssociation('affiliation', item.id) : Promise.resolve()
            }
          />
          {(parent.localGroups ?? []).length > 0 && (
            <div className='mb-3'>
              <h4 className='font-semibold mb-1'>Local Groups</h4>
              {(parent.localGroups ?? []).map((g) => (
                <div
                  key={g.id}
                  className='flex items-center justify-between border border-neutral-200 rounded px-3 py-2 mb-2'
                >
                  <span>{g.localGroup?.name}</span>
                  <Button
                    buttonType={ButtonType.tertiary}
                    aria-label={`Remove ${g.localGroup?.name ?? 'group'}`}
                    className='text-red-600 text-xl leading-none'
                    onClick={(): Promise<void> =>
                      g.id != null ? onRemoveAssociation('localGroup', g.id) : Promise.resolve()
                    }
                  >
                    &times;
                  </Button>
                </div>
              ))}
            </div>
          )}
          <AssociationTypeahead
            label='Add a local group'
            placeholder={t('pages.parentProfile.placeholders.searchLocalGroups')}
            search={async (q): Promise<Array<{ id?: number; name?: string }>> => {
              const resp = await LocalGroupService.getByFilter({ params: { name: q } });
              return (resp.data?.data ?? []) as Array<{ id?: number; name?: string }>;
            }}
            onSelect={(item): Promise<void> =>
              item.id != null ? onAddAssociation('localGroup', item.id) : Promise.resolve()
            }
          />
        </div>
      )}

      {error && <p className='text-red-600 text-sm mb-2'>{error}</p>}
    </Modal>
  );
};

// -- Flag Modal ------------------------------------------------------------
const FlagContentModal = ({
  isOpen,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reasonId: number, description: string) => Promise<void>;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [reasonId, setReasonId] = useState(0);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setReasonId(0);
      setDescription('');
      setError('');
    }
  }, [isOpen]);

  const submit = async (): Promise<void> => {
    if (!reasonId) {
      setError(t('pages.parentProfile.flagModal.reasonRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(reasonId, description);
      onClose();
    } catch (e) {
      setError(
        e instanceof AxiosError && e.response?.data?.errors?.[0]?.message
          ? e.response.data.errors[0].message
          : t('pages.parentProfile.flagModal.submitError'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.parentProfile.flagModal.title')}
      className='w-full max-w-lg'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button buttonType={ButtonType.primary} disabled={submitting} onClick={submit}>
            {t('pages.parentProfile.flagModal.submit')}
          </Button>
        </>
      }
    >
      <p className='text-sm mb-3'>
        {t('pages.parentProfile.flagModal.intro')}
      </p>
      <fieldset className='mb-3 space-y-1'>
        {PROFILE_FLAG_REASONS.map((r) => (
          <label key={r.key} className='block'>
            <input
              type='radio'
              name='flag-reason'
              checked={reasonId === r.id}
              onChange={(): void => {
                setReasonId(r.id);
                setError('');
              }}
              className='mr-2'
            />
            {t(`pages.parentProfile.flagModal.${r.key}`)}
          </label>
        ))}
      </fieldset>
      <label className='block font-semibold mb-1'>{t('pages.parentProfile.flagModal.details')}</label>
      <textarea
        className='w-full border border-neutral-300 rounded p-2'
        rows={4}
        value={description}
        onChange={(e): void => setDescription(e.target.value)}
      />
      {error && <p className='text-red-600 text-sm mt-2'>{error}</p>}
    </Modal>
  );
};

// -- Main page -------------------------------------------------------------
export const ParentProfile = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const { id: pathId } = useParams<{ id: string }>();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const [parent, setParent] = useState<ParentExtras | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [sitters, setSitters] = useState<Sitter[]>([]);
  const [openJobs, setOpenJobs] = useState<Job[]>([]);
  const [flagOpen, setFlagOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<EditSection>(null);
  const [languages, setLanguages] = useState<Language[]>([]);

  useEffect(() => {
    document.title = 'UrbanSitter - Family Profile';
  }, []);

  useEffect(() => {
    const load = async (): Promise<void> => {
      try {
        const resp = await LanguageService.getByFilter();
        setLanguages((resp.data?.data ?? []) as Language[]);
      } catch {
        setLanguages([]);
      }
    };
    void load();
  }, []);

  const refreshParent = useCallback(async (): Promise<void> => {
    const id = Number(pathId);
    if (!id || !user?.id) return;
    const resp = await ParentService.get(id, {
      params: { fields: 'badges,recommendations', userId: user.id },
    });
    setParent((resp.data?.data ?? null) as ParentExtras | null);
  }, [pathId, user?.id]);

  useEffect(() => {
    const id = Number(pathId);
    if (!id || !user?.id) return;
    const loadParent = async (): Promise<void> => {
      try {
        const resp = await ParentService.get(id, {
          params: { fields: 'badges,recommendations', userId: user.id },
        });
        setParent((resp.data?.data ?? null) as ParentExtras | null);
      } catch (err) {
        console.warn('[ParentProfile] load failed', err);
      } finally {
        setLoaded(true);
      }
    };
    const loadJobs = async (): Promise<void> => {
      try {
        const resp = await JobService.getByFilter({
          params: { parentId: id, status: 'open', sort: 'updated', limit: 6 },
        });
        setOpenJobs((resp.data?.data ?? []) as Job[]);
      } catch {
        setOpenJobs([]);
      }
    };
    void loadParent();
    void loadJobs();

    if (user.parent) {
      void (async (): Promise<void> => {
        try {
          const resp = await SitterService.getByFilter({
            params: { parentId: id, userId: user.id, fields: 'friendRelationship', booked: true },
          });
          setSitters((resp.data?.data ?? []) as Sitter[]);
        } catch {
          setSitters([]);
        }
      })();
    }
  }, [pathId, user?.id, user?.parent]);

  if (!loaded) {
    return (
      <div className='container py-8 text-center'>
        <i className='fa-solid fa-spinner fa-spin mr-2' />
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  if (!parent?.id) return <div className='container py-8' />;

  const canEdit = !!user?.id && user.id === parent.id;
  const canViewDetails = canEdit || (parent.relationship?.bookingCount ?? 0) > 0;
  const viewerIsParentSelf = canEdit;
  const viewerIsSitter = !!user?.sitter;

  const onFlag = (): void => setFlagOpen(true);
  const onShare = async (): Promise<void> => {
    const shareUrl = window.location.href;
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: `${parent.firstName}'s Profile on UrbanSitter`,
          url: shareUrl,
        });
        return;
      } catch {
        // fall through to copy
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t('pages.parentProfile.toasts.linkCopied'));
    } catch {
      toast.error(t('pages.parentProfile.toasts.shareError'));
    }
  };

  const onBlockToggle = async (blocked: boolean): Promise<void> => {
    try {
      await ParentService.patch(parent.id as number, {
        ...parent,
        relationship: { ...(parent.relationship ?? {}), blocked },
      } as unknown as Parameters<typeof ParentService.patch>[1]);
      toast.success(
        blocked
          ? t('pages.parentProfile.toasts.availabilityHidden')
          : t('pages.parentProfile.toasts.availabilityShared'),
      );
      await refreshParent();
    } catch {
      toast.error(t('pages.parentProfile.toasts.availabilityError'));
    }
  };

  const onFlagSubmit = async (reasonId: number, description: string): Promise<void> => {
    if (!user?.id) return;
    await ContentFlagService.flagProfile({
      flaggedId: parent.id as number,
      flaggingId: user.id,
      description,
      reason: { id: reasonId },
    });
    toast.success(t('pages.parentProfile.toasts.reportSubmitted'));
  };

  const onSeeMoreFeedback = (): void => {
    window.location.assign(`/app/parent/${parent.id}/reviews`);
  };

  const onEditSection = (section: string): void => {
    setEditingSection(section as EditSection);
  };

  const saveSectionPatch = async (patch: Partial<ParentExtras>): Promise<void> => {
    if (!parent?.id) return;
    // Empty patch = the modal handled persistence itself (e.g. languages calls
    // /v2/parent-languages directly); just refresh to pull fresh state.
    if (Object.keys(patch).length > 0) {
      await ParentService.patch(parent.id, { ...parent, ...patch } as never);
    }
    await refreshParent();
    toast.success(t('common.saved'));
  };

  const onRemoveAssociation = async (
    kind: 'school' | 'affiliation' | 'localGroup',
    id: number,
  ): Promise<void> => {
    try {
      if (kind === 'affiliation') {
        await ParentAffiliationService.delete(id);
      } else if (kind === 'localGroup') {
        await ParentLocalGroupsService.delete(id);
      } else {
        await ParentSchoolsService.delete(id);
      }
      await refreshParent();
      toast.success(t('pages.parentProfile.toasts.removed'));
    } catch {
      toast.error(t('pages.parentProfile.toasts.removeError'));
    }
  };

  const onAddAssociation = async (
    kind: 'school' | 'affiliation' | 'localGroup',
    entityId: number,
  ): Promise<void> => {
    if (!parent?.id) return;
    try {
      if (kind === 'school') {
        await ParentSchoolsService.create({ parentId: parent.id, schoolId: entityId });
      } else if (kind === 'affiliation') {
        await ParentAffiliationService.create({
          parentId: parent.id,
          affiliationId: entityId,
        } as never);
      } else {
        await ParentLocalGroupsService.create({
          parentId: parent.id,
          localGroupId: entityId,
        } as never);
      }
      await refreshParent();
      toast.success(t('pages.parentProfile.toasts.added'));
    } catch {
      toast.error(t('pages.parentProfile.toasts.addError'));
    }
  };

  return (
    <div className='container py-6 max-w-4xl mx-auto' id='parent-profile-page'>
      <ParentSnippet
        parent={parent}
        canEdit={canEdit}
        onFlag={onFlag}
        onShare={onShare}
        onBlockToggle={onBlockToggle}
        viewerIsSitter={viewerIsSitter}
      />

      {user?.parent && sitters.length > 0 && (
        <ParentSittersPreview sitters={sitters} parentId={parent.id} canEdit={canEdit} />
      )}

      <ParentOpenJobs jobs={openJobs} parent={parent} />
      <ParentBadgesGroups parent={parent} />

      <ParentProfileFeedback
        parent={parent}
        viewerIsParentSelf={viewerIsParentSelf}
        viewerIsSitter={viewerIsSitter}
        onSeeMore={onSeeMoreFeedback}
      />

      {canViewDetails && (
        <ParentDetail parent={parent} canEdit={canEdit} onEditSection={onEditSection} />
      )}

      <FlagContentModal
        isOpen={flagOpen}
        onClose={(): void => setFlagOpen(false)}
        onSubmit={onFlagSubmit}
      />

      <EditSectionModal
        // Re-key on section + parent.languages so the modal's local state
        // (selectedLanguageIds, kidsForms, etc.) re-initializes from the
        // latest parent every time it opens. Without this, the second open
        // shows stale values and saves don't reflect the new state.
        key={`${editingSection ?? 'closed'}:${(parent.languages ?? []).map((l) => l.id).join(',')}:${(parent.kids ?? []).length}`}
        section={editingSection}
        parent={parent}
        languages={languages}
        onClose={(): void => setEditingSection(null)}
        onSave={saveSectionPatch}
        onRemoveAssociation={onRemoveAssociation}
        onAddAssociation={onAddAssociation}
      />
    </div>
  );
});
